import React, { createContext, useContext, useState, useEffect } from "react";
import { supabase } from "../lib/supabaseClient";

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Get initial session
  useEffect(() => {
    let mounted = true;

    const getInitialSession = async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (mounted) {
          setUser(session?.user ?? null);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    getInitialSession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (mounted) {
        setUser(session?.user ?? null);
        setLoading(false);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);
  const signUpWithEmail = async (email, password, displayName) => {
    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            display_name: displayName,
          },
        },
      });

      if (error) throw error;
    } catch (error) {
      throw error;
    }
  };

  const signInWithEmail = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      // Check if user has admin/staff role
      if (data?.user) {
        const { data: roleData, error: roleError } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", data.user.id)
          .single();

        if (
          !roleError &&
          roleData &&
          (roleData.role === "admin" || roleData.role === "staff")
        ) {
          await supabase.auth.signOut();

          throw new Error(
            "This account is for admin access only. Please use the admin panel.",
          );
        }
      }
    } catch (error) {
      throw error;
    }
  };

  const signInWithGoogle = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: window.location.origin,
        },
      });

      if (error) throw error;
    } catch (error) {
      throw error;
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
  };

  const getUsername = () => {
    if (!user) return null;

    if (user.user_metadata?.display_name) {
      return user.user_metadata.display_name;
    }

    if (user.user_metadata?.full_name) {
      return user.user_metadata.full_name;
    }

    if (user.identities && user.identities.length > 0) {
      const identity = user.identities[0];

      if (identity.identity_data?.full_name) {
        return identity.identity_data.full_name;
      }
    }

    if (user.email) {
      return user.email.split("@")[0];
    }

    return "User";
  };

  const isAdmin = async () => {
    if (!user || !user.email) {
      return false;
    }

    try {
      const { data, error } = await supabase
        .from("staff_allowlist")
        .select("role")
        .eq("email", user.email)
        .single();

      if (error) {
        return false;
      }

      return data?.role === "admin";
    } catch (error) {
      return false;
    }
  };

  const value = {
    user,
    loading,
    signUpWithEmail,
    signInWithEmail,
    signInWithGoogle,
    signOut,
    getUsername,
    isAdmin,
    isAuthenticated: !!user && !user.is_anonymous,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }

  return context;
}
